// Returned to sender (Jack, 2026-11-02): a parcel we dispatched came back to
// the warehouse. Scanning its label into the Returns page's search box (a scan
// ends with Enter, which submits the search) shows this panel above the list:
// the parcel, its order, the customer, what was in the box, and one button
// that puts those items on Pending Refund for the refund manager (nothing is
// paid or sent to the channel here). The list below still searches as usual:
// a code can match both.
//
// Built for a handheld scanner at a desk: everything still refundable is
// pre-filled, and the page puts the cursor back in the search box afterwards
// so the next parcel can be scanned straight away.
//
// Why, first (Jack, 2026-11-03): "Process refund" asks for the reason — the
// quick "Returned to sender", or a typed one — and "Put £x on Pending Refund"
// stays grey until there is one. The customer is emailed later, when the
// refund manager marks the refund issued (not here: that would be premature).
// Only the scanned parcel's own contents are offered; when they were never
// recorded and the order went in several boxes, nothing is pre-filled.
//
// By order number (Jack, 2026-11-04): a return with no parcel of ours to scan
// (sent back on the customer's own label, a legacy order, one that never went
// through the returns portal) is found by typing its order number. The panel
// then shows the whole ORDER (scope "order"): every line, nothing pre-filled
// (staff enter what came back, or Select all), the quick reasons "Returned by
// the customer" and "Returned to sender", and the same Pending Refund step.
import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { PackageX, AlertTriangle, CheckCircle, ExternalLink } from "lucide-react";
import { useTranslation } from "../../i18n";
import { ReturnedParcel, ReturnedParcelLookup, lookupReturnedParcel, refundReturnedParcel, money } from "../../api/returns";

interface Props {
    /** The search as submitted. */
    code: string;
    canWrite: boolean;
    /** After a refund (or "nothing left"): the page gives the search box its focus back. */
    onRefunded?: () => void;
}

// The typed reason's length (the BE's returnedParcel.parseReason).
const REASON_MIN = 3;
const REASON_MAX = 200;

// Not worth a lookup: too short for any carrier, an email, one of our BFR- references.
export const worthLookingUp = (q: string) => q.length >= 6 && !q.includes("@") && !/^BFR-[0-9A-Z]{6}$/i.test(q);

const orderLink = (orderId: string) => `/admin/warehouse/orders?order=${encodeURIComponent(orderId)}`;

type Done = { kind: "done"; amount: number; units: number; orderId: string; orderNumber: string; parts: string[] } | { kind: "already" } | null;
type QuickReason = "returned_to_sender" | "returned_by_customer";

// A whole order, found by its number (no parcel of ours).
const isWholeOrder = (p: ReturnedParcel | null | undefined) => p?.scope === "order";

const ReturnedParcelPanel = ({ code, canWrite, onRefunded }: Props) => {
    const { t, language } = useTranslation();
    const [found, setFound] = useState<ReturnedParcelLookup | null>(null);
    const [index, setIndex] = useState(0);
    const [qty, setQty] = useState<Record<number, number>>({});
    // The reason step: open after "Process refund"; the quick reason or typed text.
    const [processing, setProcessing] = useState(false);
    const [quickReason, setQuickReason] = useState<QuickReason | null>(null);
    const [reasonText, setReasonText] = useState("");
    const [busy, setBusy] = useState(false);
    const [done, setDone] = useState<Done>(null);
    const [error, setError] = useState<string | null>(null);

    const when = useMemo(() => new Intl.DateTimeFormat(language === "pl" ? "pl-PL" : "en-GB", {
        day: "numeric", month: "short", hour: "2-digit", minute: "2-digit",
    }), [language]);
    const at = (v: string | null | undefined) => (v ? when.format(new Date(v)) : "");

    const closeReason = () => {
        setProcessing(false);
        setQuickReason(null);
        setReasonText("");
    };

    const reset = (lookup: ReturnedParcelLookup | null) => {
        setFound(lookup);
        setIndex(0);
        setQty(defaults(lookup?.parcels?.[0] ?? null));
        closeReason();
        setDone(null);
        setError(null);
    };

    useEffect(() => {
        let alive = true;
        const q = code.trim();
        reset(null);
        if (!worthLookingUp(q)) return () => { alive = false; };
        // A BE without the endpoint (404, no code) or a failed read: no panel, the list still works.
        lookupReturnedParcel(q).then((r) => { if (alive && r.ok && r.data) reset(r.data); });
        return () => { alive = false; };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [code]);

    if (!found) return null;
    if (!found.found) {
        if (!found.order) return null;
        return (
            <section className="rt-rts" data-testid="rt-rts">
                <p className="rt-warn"><AlertTriangle size={14} /> {t("rt_rts_no_parcels", { order: found.order.order_number ?? found.order.order_id })}</p>
            </section>
        );
    }
    const parcels = found.parcels ?? [];
    const p = parcels[Math.min(index, parcels.length - 1)];
    if (!p) return null;

    const cur = p.order.currency;
    const units = p.lines.reduce((s, l) => s + (l.order_item_id ? qty[l.order_item_id] ?? 0 : 0), 0);
    const total = p.lines.reduce((s, l) => s + (l.order_item_id ? (qty[l.order_item_id] ?? 0) * l.unit_price_minor : 0), 0);
    const orderNo = p.order.order_number ?? p.order.order_id;
    const write = canWrite && p.can_refund;
    const typed = reasonText.replace(/\s+/g, " ").trim();
    const reasonReady = quickReason !== null || typed.length >= REASON_MIN;
    const wholeOrder = isWholeOrder(p);
    const contentsUnknown = !wholeOrder && !p.contents_recorded && p.parcel_count > 1;
    // The quick reasons: a whole order also came back from the customer themselves.
    const quickReasons: { code: QuickReason; label: string }[] = wholeOrder
        ? [{ code: "returned_by_customer", label: t("rt_rts_reason_customer") }, { code: "returned_to_sender", label: t("rt_rts_reason_rts") }]
        : [{ code: "returned_to_sender", label: t("rt_rts_reason_rts") }];
    const selectAll = () => setQty(Object.fromEntries(p.lines.filter((l) => l.order_item_id && l.refundable > 0).map((l) => [l.order_item_id as number, l.refundable])));

    const pick = (i: number) => {
        setIndex(i);
        setQty(defaults(parcels[i] ?? null));
        closeReason();
        setDone(null);
        setError(null);
    };

    const setLineQty = (id: number, max: number, raw: string) => {
        const n = Math.max(0, Math.min(max, Math.floor(Number(raw) || 0)));
        setQty((prev) => ({ ...prev, [id]: n }));
    };

    const submit = async () => {
        if (!reasonReady) return;
        setBusy(true);
        setError(null);
        try {
            const lines = p.lines
                .filter((l) => l.order_item_id && (qty[l.order_item_id] ?? 0) > 0)
                .map((l) => ({ order_item_id: l.order_item_id as number, qty: qty[l.order_item_id as number] }));
            const r = await refundReturnedParcel({
                ...(wholeOrder || !p.tracking_number ? { order_id: p.order.order_id } : { tracking_number: p.tracking_number }),
                ...(quickReason ? { reason_code: quickReason } : { reason: typed }),
                lines,
            });
            const fresh = r.ok ? r.data?.parcel : r.body?.parcel;
            if (fresh) {
                const next = parcels.map((x, i) => (i === index ? (fresh as ReturnedParcel) : x));
                setFound({ ...found, parcels: next });
                setQty(defaults(fresh as ReturnedParcel));
            }
            if (!r.ok || !r.data) {
                setError(r.message || t("rt_rts_failed"));
                return;
            }
            const d = r.data;
            closeReason();
            if (d.units > 0) {
                const parts = (d.orders ?? []).filter((o) => o.order_id !== d.pending_on?.order_id).map((o) => `#${o.order_number ?? o.order_id}`);
                setDone({
                    kind: "done", amount: d.amount_minor, units: d.units,
                    orderId: d.pending_on?.order_id ?? p.order.order_id, orderNumber: d.pending_on?.order_number ?? orderNo, parts,
                });
            } else {
                setDone({ kind: "already" });
            }
            onRefunded?.();
        } finally {
            setBusy(false);
        }
    };

    const statusText = p.status ? t(`parcel_status_${p.status}`) : "-";
    const refuseText = (): string | null => {
        if (!p.refuse) return null;
        switch (p.refuse.code) {
            case "not_channel": return t("rt_rts_not_channel", { channel: p.order.channel_name ?? "-" });
            case "replacement_order": return t("rt_rts_replacement", { order: p.order.replacement_of?.order_number ?? p.order.replacement_of?.order_id ?? "-" });
            case "label_voided": return t("rt_rts_label_voided");
            case "order_status": return t("rt_rts_order_status", { status: p.order.status ? t(`status_${p.order.status}`) : "-" });
            case "nothing_left": return t(wholeOrder ? "rt_rts_nothing_left_order" : "rt_rts_nothing_left");
            default: return p.refuse.message;
        }
    };
    const refuse = refuseText();

    return (
        <section className="rt-rts" aria-label={t(wholeOrder ? "rt_rts_title_order" : "rt_rts_title")} data-testid="rt-rts">
            <div className="rt-rts-head">
                <h2 className="rt-rts-title">
                    <PackageX size={16} aria-hidden="true" /> {t(wholeOrder ? "rt_rts_title_order" : "rt_rts_title")}
                    {p.order.channel_name && <span className="rt-pill rt-pill--plain">{p.order.channel_name}</span>}
                    {!wholeOrder && (p.returned_to_sender
                        ? <span className="rt-pill rt-pill--amber">{t("rt_rts_returned")}</span>
                        : <span className="rt-pill rt-pill--grey" title={p.carrier_status ?? undefined}>{t("rt_rts_tracking", { status: statusText })}</span>)}
                </h2>
                <span className="rt-mono rt-muted">{wholeOrder ? `#${orderNo}` : p.tracking_number}</span>
            </div>

            {found.superseded && <p className="rt-warn"><AlertTriangle size={14} /> {t("rt_rts_superseded")}</p>}
            {parcels.length > 1 && (
                <div className="rt-rts-pick">
                    <span className="rt-muted rt-small">{t("rt_rts_matches", { n: String(parcels.length) })}</span>
                    <span className="rt-links">
                        {parcels.map((x, i) => {
                            const name = x.tracking_number ?? `#${x.order.order_number ?? x.order.order_id}`;
                            return (
                                <button key={`${x.order.order_id}-${name}`} type="button" className="rt-chip rt-mono" aria-pressed={i === index} onClick={() => pick(i)}>
                                    {name}
                                </button>
                            );
                        })}
                    </span>
                </div>
            )}

            <div className="rt-rts-meta">
                <div>
                    <span className="rt-label">{t("rt_rts_order")}</span>
                    <Link className="rt-ref-link" to={orderLink(p.order.order_id)}>#{orderNo} <ExternalLink size={12} /></Link>
                    {p.order.parts.length > 0 && (
                        <span className="rt-muted rt-small">{t("rt_rts_parts", { orders: p.order.parts.map((x) => `#${x.order_number ?? x.order_id}`).join(", ") })}</span>
                    )}
                </div>
                <div>
                    <span className="rt-label">{t("rt_rts_customer")}</span>
                    <span className="rt-ellipsis">{p.customer.name ?? "-"}</span>
                    <span className="rt-muted rt-small rt-ellipsis">{[p.customer.email, p.customer.postcode].filter(Boolean).join(" · ")}</span>
                </div>
                {wholeOrder ? (
                    <div>
                        <span className="rt-label">{t("rt_rts_sent_in")}</span>
                        {(p.order_parcels ?? []).length === 0 && <span className="rt-muted">{t("rt_rts_no_parcels_recorded")}</span>}
                        {(p.order_parcels ?? []).map((x) => (
                            <span key={x.tracking_number} className="rt-muted rt-small rt-block">
                                {[x.carrier, x.tracking_number].filter(Boolean).join(" · ")}
                            </span>
                        ))}
                    </div>
                ) : (
                    <div>
                        <span className="rt-label">{t("rt_rts_parcel")}</span>
                        <span>{[p.carrier, p.service].filter(Boolean).join(" · ") || "-"}</span>
                        <span className="rt-muted rt-small">
                            {p.box_position ? t("rt_rts_box_of", { n: String(p.box_position), total: String(Math.max(p.parcel_count, p.box_position)) }) : ""}
                            {p.shipped_at ? `${p.box_position ? " · " : ""}${t("rt_rts_shipped", { date: at(p.shipped_at) })}` : ""}
                        </span>
                    </div>
                )}
            </div>

            {refuse && p.refuse?.code !== "nothing_left" && <p className="rt-warn" role="note"><AlertTriangle size={14} /> {refuse}</p>}
            {wholeOrder && p.can_refund && <p className="rt-muted rt-small" data-testid="rt-rts-order-found">{t("rt_rts_order_found")}</p>}
            {!wholeOrder && !p.contents_recorded && (
                <p className="rt-warn"><AlertTriangle size={14} /> {contentsUnknown
                    ? t("rt_rts_contents_not_recorded_many", { n: String(p.parcel_count) })
                    : t("rt_rts_contents_not_recorded")}</p>
            )}
            {p.history.map((h, i) => (
                <p key={i} className="rt-muted rt-small rt-rts-history">
                    {t("rt_rts_history", { amount: money(h.amount_minor, cur), n: String(h.units), when: at(h.at), by: h.by ?? "-" })}
                    {h.reason ? ` · ${h.reason}` : ""}
                    {wholeOrder && h.tracking_number ? ` · ${t("rt_rts_history_parcel", { tracking: h.tracking_number })}` : ""}
                </p>
            ))}

            <div className="rt-rts-lines">
                {p.lines.map((l, i) => {
                    const id = l.order_item_id;
                    const n = id ? qty[id] ?? 0 : 0;
                    return (
                        <div key={id ?? `x-${i}`} className="rt-rts-line" data-testid="rt-rts-line">
                            {l.image_url ? <img className="rt-line-img" src={l.image_url} alt="" /> : <span className="rt-line-img" />}
                            <div className="rt-line-main">
                                <strong>{l.title ?? l.sku}</strong>
                                <span className="rt-mono rt-muted">
                                    {l.sku} · {t(wholeOrder ? "rt_rts_ordered" : "rt_rts_in_box", { n: String(wholeOrder ? l.quantity : l.in_parcel) })} · {t("rt_rts_each", { price: money(l.unit_price_minor, cur) })}
                                    {l.part_order_number ? ` · #${l.part_order_number}` : ""}
                                </span>
                                {(l.refunded > 0 || l.pending > 0) && (
                                    <span className="rt-muted rt-small">{t("rt_rts_refunded_pending", { refunded: String(l.refunded), pending: String(l.pending) })}</span>
                                )}
                            </div>
                            <label className="rt-rts-qty">
                                {id && l.refundable > 0 ? (
                                    <>
                                        <input type="number" className="rt-input" min={0} max={l.refundable} step={1} value={n}
                                            disabled={!write || busy} aria-label={t("rt_rts_qty_label", { sku: l.sku })}
                                            onChange={(e) => setLineQty(id, l.refundable, e.target.value)} />
                                        <span className="rt-muted">{t("rt_rts_of", { n: String(l.refundable) })}</span>
                                    </>
                                ) : <span className="rt-muted">{t("rt_rts_none_left")}</span>}
                            </label>
                            <div className="rt-line-money"><strong>{money(n * l.unit_price_minor, cur)}</strong></div>
                        </div>
                    );
                })}
            </div>

            {p.can_refund && p.order.delivery_paid_minor > 0 && (
                <p className="rt-muted rt-small">{t("rt_rts_delivery", { amount: money(p.order.delivery_paid_minor, cur) })}</p>
            )}

            <div className="rt-rts-foot">
                <span className="rt-rts-total">
                    {t("rt_rts_total")}: <strong>{money(total, cur)}</strong> <span className="rt-muted">({t("rt_units", { n: String(units) })})</span>
                </span>
                {wholeOrder && p.can_refund && !processing && (
                    <button type="button" className="btn btn-sm rt-btn btn-ghost" disabled={!write || busy} onClick={selectAll}>{t("rt_rts_select_all")}</button>
                )}
                {!processing && p.can_refund && (
                    <button type="button" className="btn btn-sm rt-btn btn-primary" disabled={!write || busy || units === 0}
                        title={!canWrite ? t("read_only_mode") : undefined} onClick={() => setProcessing(true)}>
                        {t("rt_rts_process")}
                    </button>
                )}
                {p.refuse?.code === "nothing_left" && <span className="rt-muted">{t(wholeOrder ? "rt_rts_nothing_left_order" : "rt_rts_nothing_left")}</span>}
            </div>
            {processing && (
                <div className="rt-rts-confirm" role="group" aria-label={t("rt_rts_why")}>
                    <p className="rt-rts-why">{t("rt_rts_why")}</p>
                    <div className="rt-rts-reason">
                        {quickReasons.map((r) => (
                            <button key={r.code} type="button" className="rt-chip" aria-pressed={quickReason === r.code} disabled={busy}
                                onClick={() => { setQuickReason((on) => (on === r.code ? null : r.code)); setReasonText(""); }}>
                                <PackageX size={14} aria-hidden="true" /> {r.label}
                            </button>
                        ))}
                        <input className="rt-input" value={reasonText} maxLength={REASON_MAX} disabled={busy} autoComplete="off"
                            placeholder={t("rt_rts_reason_placeholder")} aria-label={t("rt_rts_reason_label")}
                            onChange={(e) => { setReasonText(e.target.value); if (e.target.value.trim()) setQuickReason(null); }}
                            onKeyDown={(e) => { if (e.key === "Enter" && reasonReady && !busy) void submit(); }} />
                    </div>
                    <p className="rt-muted rt-small">{t("rt_rts_reason_hint")}</p>
                    <div className="rt-rts-confirm-btns">
                        {/* Grey until there is a reason, orange once there is (Jack, 2026-11-03). */}
                        <button type="button" className="btn btn-sm rt-btn btn-primary" disabled={!write || busy || units === 0 || !reasonReady}
                            title={!reasonReady ? t("rt_rts_reason_needed") : undefined} onClick={submit}>
                            {t("rt_rts_put", { amount: money(total, cur) })}
                        </button>
                        <button type="button" className="btn btn-sm rt-btn btn-ghost" disabled={busy} onClick={closeReason}>{t("cancel")}</button>
                    </div>
                </div>
            )}
            {error && <p className="rt-error" role="alert">{error}</p>}
            {done?.kind === "done" && (
                <p className="rt-rts-done" role="status">
                    <CheckCircle size={14} aria-hidden="true" />
                    <span>
                        {t("rt_rts_done", { amount: money(done.amount, cur), n: String(done.units), order: done.orderNumber })}
                        {done.parts.length > 0 ? ` ${t("rt_rts_parts", { orders: done.parts.join(", ") })}` : ""}
                        {" "}<Link className="rt-ref-link" to={orderLink(done.orderId)}>{t("rt_rts_open_order")} <ExternalLink size={12} /></Link>
                    </span>
                </p>
            )}
            {done?.kind === "already" && <p className="rt-muted" role="status">{t("rt_rts_already")}</p>}
        </section>
    );
};

// Everything still refundable, line by line. Not when the box's contents were
// never recorded and the order went in several boxes: which units were in
// THIS one is unknown, so staff set them from what is in front of them.
// A whole order: nothing either, the customer may have sent back only some of it.
const defaults = (p: ReturnedParcel | null): Record<number, number> => {
    const out: Record<number, number> = {};
    if (isWholeOrder(p)) return out;
    if (p && !p.contents_recorded && p.parcel_count > 1) return out;
    for (const l of p?.lines ?? []) if (l.order_item_id && l.refundable > 0) out[l.order_item_id] = l.refundable;
    return out;
};

export default ReturnedParcelPanel;
